// confetti.js - 作为 ES6 模块

// 工具函数与常量（模块内部私有，不暴露）
const { random, cos, sin, PI, floor: MathFloor } = Math;
const PI2 = PI * 2;

// 全局状态（模块内部私有）
let timer = undefined;
let frame = undefined;
let confetti = [];

// 配置参数（模块内部私有，如需外部修改可改为暴露的变量）
const particles = 150;
const spread = 40;
const sizeMin = 3;
const sizeMax = 12 - sizeMin;
const eccentricity = 10;
const deviation = 100;
const dxThetaMin = -0.1;
const dxThetaMax = -dxThetaMin - dxThetaMin;
const dyMin = 0.13;
const dyMax = 0.18;
const dThetaMin = 0.4;
const dThetaMax = 0.7 - dThetaMin;

// 颜色工具函数（内部私有）
function color(r, g, b) {
    return `rgb(${r}, ${g}, ${b})`;
}

// 颜色主题（内部私有）
const colorThemes = [
    () => color(MathFloor(200 * random()), MathFloor(200 * random()), MathFloor(200 * random())),
    () => { const black = MathFloor(200 * random()); return color(200, black, black); },
    () => { const black = MathFloor(200 * random()); return color(black, 200, black); },
    () => { const black = MathFloor(200 * random()); return color(black, black, 200); },
    () => color(200, 100, MathFloor(200 * random())),
    () => color(MathFloor(200 * random()), 200, 200),
    () => { const black = MathFloor(256 * random()); return color(black, black, black); },
    () => colorThemes[random() < 0.5 ? 1 : 2](),
    () => colorThemes[random() < 0.5 ? 3 : 5](),
    () => colorThemes[random() < 0.5 ? 2 : 4]()
];

// 插值函数（内部私有）
function interpolation(a, b, t) {
    return (1 - cos(PI * t)) / 2 * (b - a) + a;
}

// Poisson 曲线生成（内部私有）
const radius = 1 / eccentricity;
const radius2 = radius + radius;
function createPoisson() {
    const domain = [radius, 1 - radius];
    let measure = 1 - radius2;
    const spline = [0, 1];

    while (measure) {
        let dart = measure * random();
        let i, len, interval, a, b, c, d;

        for (i = 0, len = domain.length, measure = 0; i < len; i += 2) {
            a = domain[i];
            b = domain[i + 1];
            interval = b - a;
            if (dart < measure + interval) {
                spline.push(dart += a - measure);
                break;
            }
            measure += interval;
        }

        c = dart - radius;
        d = dart + radius;

        for (i = domain.length - 1; i > 0; i -= 2) {
            len = i - 1;
            a = domain[len];
            b = domain[i];
            if (a >= c && a < d) {
                if (b > d) domain[len] = d;
                else domain.splice(len, 2);
            } else if (a < c && b > c) {
                if (b <= d) domain[i] = c;
                else domain.splice(i, 0, c, d);
            }
        }

        for (i = 0, len = domain.length, measure = 0; i < len; i += 2) {
            measure += domain[i + 1] - domain[i];
        }
    }

    return spline.sort();
}

// 容器元素（内部私有）
const container = document.createElement('div');
Object.assign(container.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '0',
    overflow: 'visible',
    zIndex: '9999'
});

// Confetto 类（内部私有）
class Confetto {
    constructor(theme) {
        this.frame = 0;
        this.outer = document.createElement('div');
        this.inner = document.createElement('div');
        this.outer.appendChild(this.inner);

        const outerStyle = this.outer.style;
        const innerStyle = this.inner.style;

        outerStyle.position = 'absolute';
        outerStyle.width = `${sizeMin + sizeMax * random()}px`;
        outerStyle.height = `${sizeMin + sizeMax * random()}px`;
        innerStyle.width = '100%';
        innerStyle.height = '100%';
        innerStyle.backgroundColor = theme();
        outerStyle.perspective = '50px';
        outerStyle.transform = `rotate(${360 * random()}deg)`;

        this.axis = `rotate3D(${cos(360 * random())},${cos(360 * random())},0,`;
        this.theta = 360 * random();
        this.dTheta = dThetaMin + dThetaMax * random();
        innerStyle.transform = `${this.axis}${this.theta}deg)`;

        this.x = window.innerWidth * random();
        this.y = -deviation;
        this.dx = sin(dxThetaMin + dxThetaMax * random());
        this.dy = dyMin + dyMax * random();
        outerStyle.left = `${this.x}px`;
        outerStyle.top = `${this.y}px`;

        this.splineX = createPoisson();
        this.splineY = [];
        const splineXLen = this.splineX.length - 1;
        for (let i = 1; i < splineXLen; ++i) {
            this.splineY[i] = deviation * random();
        }
        this.splineY[0] = this.splineY[splineXLen] = deviation * random();
    }

    update(height, delta) {
        this.frame += delta;
        this.x += this.dx * delta;
        this.y += this.dy * delta;
        this.theta += this.dTheta * delta;

        const phi = this.frame % 7777 / 7777;
        let i = 0, j = 1;
        while (phi >= this.splineX[j]) {
            i = j++;
        }

        const rho = interpolation(
            this.splineY[i],
            this.splineY[j],
            (phi - this.splineX[i]) / (this.splineX[j] - this.splineX[i])
        );
        const adjustedPhi = phi * PI2;

        this.outer.style.left = `${this.x + rho * cos(adjustedPhi)}px`;
        this.outer.style.top = `${this.y + rho * sin(adjustedPhi)}px`;
        this.inner.style.transform = `${this.axis}${this.theta}deg)`;

        return this.y > height + deviation;
    }
}


function poof() {
    if (!frame) {
        document.body.appendChild(container);

        const theme = colorThemes[MathFloor(colorThemes.length * random())]; // 简化逻辑，直接随机主题
        let count = 0;

        const addConfetto = () => {
            if (++count > particles) {
                timer = undefined;
                return;
            }

            const confetto = new Confetto(theme);
            confetti.push(confetto);
            container.appendChild(confetto.outer);
            timer = setTimeout(addConfetto, spread * random());
        };

        addConfetto();

        let prev;
        requestAnimationFrame(function loop(timestamp) {
            const delta = prev ? timestamp - prev : 0;
            prev = timestamp;
            const height = window.innerHeight;

            for (let i = confetti.length - 1; i >= 0; --i) {
                if (confetti[i].update(height, delta)) {
                    container.removeChild(confetti[i].outer);
                    confetti.splice(i, 1);
                }
            }

            if (timer || confetti.length) {
                frame = requestAnimationFrame(loop);
                return;
            }

            document.body.removeChild(container);
            frame = undefined;
        });
    }
}

window.onload = () => {
    poof();
};
